import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const ChartBuilder = () => {
  // Données et options
  const [title, setTitle] = useState('');
  const [metal, setMetal] = useState('or');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [chartConfig, setChartConfig] = useState(null);
  const [chartId, setChartId] = useState(null);
  const [error, setError] = useState('');

  // Options de design
  const [showPoints, setShowPoints] = useState(true);
  const [pointColor, setPointColor] = useState('#ff0000');
  const [lineColor, setLineColor] = useState('#0000ff');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [gridColor, setGridColor] = useState('#cccccc');
  const [tickColor, setTickColor] = useState('#333333');

  // Logo et upload
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPosition, setLogoPosition] = useState('center'); // center, topLeft, topRight, bottomLeft, bottomRight
  const [logoOpacity, setLogoOpacity] = useState(0.5);
  // State pour stocker l'image préchargée
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Précharger l'image logo lorsque logoUrl change
  useEffect(() => {
    if (logoUrl) {
      const img = new Image();
      img.src = logoUrl;
      img.onload = () => {
        console.log("Image chargée correctement :", logoUrl);
        setBackgroundImage(img);
      };
      img.onerror = (e) => {
        console.error("Erreur lors du chargement de l'image :", e);
        setError("Erreur lors du chargement de l'image");
      };
    } else {
      setBackgroundImage(null);
    }
  }, [logoUrl]);

  // Fonction pour uploader le logo (drag & drop)
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const response = await axios.post('/api/upload/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log("Logo uploadé :", response.data.url);
      setLogoUrl(response.data.url);
    } catch (err) {
      console.error("Erreur d'upload du logo:", err);
      setError("Erreur lors de l'upload du logo.");
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // Chargement des données depuis l'API
  const loadData = async () => {
    if (!dateRange.start) {
      setError("Veuillez spécifier une date de départ.");
      return;
    }
    try {
      // Exemple de mapping : 'or' => 1, 'argent' => 2, 'numismatique' => 3
      const metalId = metal === 'or' ? 1 : metal === 'argent' ? 2 : metal === 'numismatique' ? 3 : 1;
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/metals/daily', {
        params: { date: dateRange.start, metalId },
        headers: { Authorization: `Bearer ${token}` }
      });
      const dataPoints = response.data; // Doit être un tableau de points { datetime, valeur }
      if (!Array.isArray(dataPoints)) {
        setError("Format des données incorrect.");
        return;
      }
      const labels = dataPoints.map(p => p.datetime);
      const data = dataPoints.map(p => parseFloat(p.valeur));
      setChartConfig({
        labels,
        datasets: [{
          label: `Cours de ${metal} le ${dateRange.start}`,
          data,
          fill: false,
          borderColor: lineColor,
          backgroundColor: pointColor,
          pointRadius: showPoints ? 5 : 0,
        }]
      });
      setError('');
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des données.");
    }
  };

  // Sauvegarder le chart
  const saveChart = async () => {
    if (!chartConfig) {
      setError("Aucune configuration à sauvegarder.");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const configToSave = { title, metal, dateRange, chartConfig };
      const response = await axios.post('/api/charts', configToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartId(response.data.id);
      alert("Graphique sauvegardé !");
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la sauvegarde du graphique.");
    }
  };

  // Mise à jour de la config lors du changement d'options
  const updateChartConfig = () => {
    if (!chartConfig || !chartConfig.labels || !chartConfig.datasets) return;
    const updatedDatasets = chartConfig.datasets.map(ds => ({
      ...ds,
      borderColor: lineColor,
      backgroundColor: pointColor,
      pointRadius: showPoints ? 5 : 0,
    }));
    setChartConfig({ ...chartConfig, datasets: updatedDatasets });
  };

  useEffect(() => {
    updateChartConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineColor, pointColor, showPoints]);

  // Préparation du plugin pour afficher le logo en arrière-plan (utilise l'image préchargée)
  const plugins = [];
  if (backgroundImage) {
    plugins.push({
      id: 'backgroundImage',
      beforeDraw: (chart) => {
        const { ctx, width, height } = chart;
        let x = 0, y = 0;
        switch (logoPosition) {
          case 'topLeft':
            x = 0;
            y = 0;
            break;
          case 'topRight':
            x = width - backgroundImage.width;
            y = 0;
            break;
          case 'bottomLeft':
            x = 0;
            y = height - backgroundImage.height;
            break;
          case 'bottomRight':
            x = width - backgroundImage.width;
            y = height - backgroundImage.height;
            break;
          case 'center':
          default:
            x = (width - backgroundImage.width) / 2;
            y = (height - backgroundImage.height) / 2;
            break;
        }
        ctx.globalAlpha = logoOpacity;
        ctx.drawImage(backgroundImage, x, y, backgroundImage.width, backgroundImage.height);
        ctx.globalAlpha = 1;
      }
    });
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h2>Chart Builder</h2>
      {error && <div style={{ color: 'red' }}>{error}</div>}
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <label>Titre :</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div>
          <label>Métal :</label>
          <select value={metal} onChange={e => setMetal(e.target.value)}>
            <option value="or">Or</option>
            <option value="argent">Argent</option>
            <option value="numismatique">Numismatique</option>
          </select>
        </div>
        <div>
          <label>Date de départ :</label>
          <input type="date" value={dateRange.start} onChange={e => setDateRange({ ...dateRange, start: e.target.value })} />
        </div>
        <div>
          <button onClick={loadData}>Charger données</button>
        </div>
      </div>
      
      <hr style={{ margin: '20px 0' }} />
      
      <h3>Personnalisation du Graphique</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <label>
            Afficher points :
            <input type="checkbox" checked={showPoints} onChange={e => setShowPoints(e.target.checked)} />
          </label>
        </div>
        <div>
          <label>
            Couleur points :
            <input type="color" value={pointColor} onChange={e => setPointColor(e.target.value)} />
          </label>
        </div>
        <div>
          <label>
            Couleur courbe :
            <input type="color" value={lineColor} onChange={e => setLineColor(e.target.value)} />
          </label>
        </div>
        <div>
          <label>
            Couleur de fond :
            <input type="color" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)} />
          </label>
        </div>
        <div>
          <label>
            Couleur grilles :
            <input type="color" value={gridColor} onChange={e => setGridColor(e.target.value)} />
          </label>
        </div>
        <div>
          <label>
            Couleur ticks :
            <input type="color" value={tickColor} onChange={e => setTickColor(e.target.value)} />
          </label>
        </div>
      </div>
      
      <hr style={{ margin: '20px 0' }} />
      
      <h3>Upload & Positionnement du Logo</h3>
      <div {...getRootProps()} style={{ border: '2px dashed #aaa', padding: '20px', textAlign: 'center', cursor: 'pointer' }}>
        <input {...getInputProps()} />
        {isDragActive ? <p>Déposez le fichier ici...</p> : <p>Glissez-déposez le logo ici ou cliquez pour sélectionner</p>}
      </div>
      {logoUrl && (
        <div>
          <p>Logo uploadé : {logoUrl}</p>
          <div>
            <label>
              Position du logo :
              <select value={logoPosition} onChange={e => setLogoPosition(e.target.value)}>
                <option value="center">Centre</option>
                <option value="topLeft">Haut gauche</option>
                <option value="topRight">Haut droite</option>
                <option value="bottomLeft">Bas gauche</option>
                <option value="bottomRight">Bas droite</option>
              </select>
            </label>
          </div>
          <div>
            <label>
              Opacité du logo :
              <input type="range" min="0" max="1" step="0.1" value={logoOpacity} onChange={e => setLogoOpacity(parseFloat(e.target.value))} />
            </label>
          </div>
        </div>
      )}
      
      <hr style={{ margin: '20px 0' }} />
      
      <div style={{ backgroundColor: backgroundColor, padding: '20px', maxWidth: '800px', margin: '20px auto' }}>
        {chartConfig ? (
          <Line
            data={chartConfig}
            options={{
              scales: {
                x: { grid: { color: gridColor }, ticks: { color: tickColor } },
                y: { grid: { color: gridColor }, ticks: { color: tickColor } }
              }
            }}
            plugins={plugins}
          />
        ) : (
          <p>Pas encore de graphique. Chargez les données.</p>
        )}
      </div>
      
      <div style={{ marginTop: '20px' }}>
        <button onClick={saveChart}>Sauvegarder le graphique</button>
        {chartId && (
          <div>
            <p>Code Iframe :</p>
            <code>{`<iframe src="https://votredomaine.com/charts/embed/${chartId}" width="600" height="400" frameBorder="0"></iframe>`}</code>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartBuilder;
